import {
  PILLAR_KEYS_FOR_CHARTS,
  getPillarScore,
  getPillarImportance,
  getPillarAgreement,
  orgHasImportanceScores,
  orgHasAgreementScores,
} from "./companyInsightsConstants";

const MAX_OVERALL_RANK_CHART_ORGS = 10;

function scopeFilters(scopeLine, industry, companySize, dateFrom, dateTo) {
  return {
    industry,
    companySize,
    dateFrom,
    dateTo,
    scope: scopeLine,
  };
}

function resolvePillarKeys(activePillarKeys) {
  return activePillarKeys?.length ? activePillarKeys : PILLAR_KEYS_FOR_CHARTS;
}

function pillarHighLow(org, activePillarKeys) {
  const keys = resolvePillarKeys(activePillarKeys);
  const rows = keys
    .map(({ key, label }) => ({
      label,
      value: getPillarScore(org, key),
    }))
    .filter((p) => p.value != null && !Number.isNaN(p.value));
  if (!rows.length) return { strongest: null, weakest: null };
  const sorted = [...rows].sort((a, b) => b.value - a.value);
  return {
    strongest: { label: sorted[0].label, value: Math.round(sorted[0].value) },
    weakest: {
      label: sorted[sorted.length - 1].label,
      value: Math.round(sorted[sorted.length - 1].value),
    },
  };
}

/** Report-ready orgs on chart: focus first, then compare selection order. */
function collectChartOrgs(focusOrg, compareOrgs, compareIds, isReady) {
  const orgs = [];
  const add = (o) => {
    if (o && isReady(o) && !orgs.some((x) => x.id === o.id)) orgs.push(o);
  };
  if (focusOrg) add(focusOrg);
  const byId = new Map();
  (compareOrgs || []).forEach((o) => byId.set(o.id, o));
  if (compareIds?.length) {
    compareIds.forEach((id) => add(byId.get(id)));
  } else {
    (compareOrgs || []).forEach((o) => add(o));
  }
  return orgs;
}

function pillarProfileCompanyLine(c) {
  const sp = c.strongest;
  const wp = c.weakest;
  if (!sp && !wp) return null;
  let line = `${c.name} —`;
  if (sp) line += ` strongest pillar: ${sp.label} (${sp.value})`;
  if (wp && sp && wp.label !== sp.label) line += `, weakest: ${wp.label} (${wp.value})`;
  return line;
}

function importanceCompanyLine(c) {
  const hi = c.highest;
  const lo = c.lowest;
  if (!hi) return null;
  let line = `${c.name} — highest importance: ${hi.label} (${hi.value})`;
  if (lo && lo.label !== hi.label) line += `, lowest: ${lo.label} (${lo.value})`;
  return line;
}

function agreementCompanyLine(c) {
  const low = c.lowestAgreement;
  const high = c.highestAgreement;
  if (!low) return null;
  let line = `${c.name} — lowest agreement: ${low.label} (${low.value})`;
  if (high && high.label !== low.label) line += `, highest: ${high.label} (${high.value})`;
  return line;
}

function buildPerformanceRankingContext({
  analysisReportReady,
  workspacePercentileById,
  focusOrgId,
  compareIds,
  industry,
  companySize,
  dateFrom,
  dateTo,
  scopeLine,
}) {
  const displaySortedDesc = [...analysisReportReady].sort(
    (a, b) => b.overall - a.overall || String(a.name).localeCompare(String(b.name))
  );
  const cohortDefault = !focusOrgId && compareIds.length === 0;
  const displayList =
    cohortDefault && displaySortedDesc.length > MAX_OVERALL_RANK_CHART_ORGS
      ? displaySortedDesc.slice(0, MAX_OVERALL_RANK_CHART_ORGS)
      : displaySortedDesc;

  const ranked = displayList.map((o) => ({
    name: o.name,
    overall: Math.round(o.overall),
    percentile: workspacePercentileById.get(o.id) ?? null,
  }));

  const best = ranked[0] || null;
  const worst = ranked.length ? ranked[ranked.length - 1] : null;

  return {
    chartKey: "performance_ranking",
    chartTitle: "Performance ranking",
    whatThisShows:
      "This visual shows the top companies based on their overall score, along with each company's percentile versus all report-ready companies in the workspace.",
    filters: scopeFilters(scopeLine, industry, companySize, dateFrom, dateTo),
    insights: {
      companyCount: ranked.length,
      ranked,
      best,
      worst,
      spread:
        best && worst && best.name !== worst.name
          ? best.overall - worst.overall
          : null,
    },
  };
}

function buildScoreBandContext({
  analysisReportReady,
  industry,
  companySize,
  dateFrom,
  dateTo,
  scopeLine,
}) {
  const labels = ["0–20", "21–40", "41–60", "61–80", "81–100"];
  const bucket = (s) => {
    if (s <= 20) return 0;
    if (s <= 40) return 1;
    if (s <= 60) return 2;
    if (s <= 80) return 3;
    return 4;
  };
  const bucketDetails = labels.map(() => []);
  analysisReportReady.forEach((o) => {
    bucketDetails[bucket(o.overall)].push({
      name: o.name,
      score: Math.round(o.overall),
    });
  });
  const counts = bucketDetails.map((arr) => arr.length);
  let densestIdx = 0;
  counts.forEach((c, i) => {
    if (c > counts[densestIdx]) densestIdx = i;
  });

  return {
    chartKey: "score_band",
    chartTitle: "Report-ready companies by score band",
    whatThisShows:
      "This visual shows how report-ready companies are distributed across overall score bands (0–20 up to 81–100).",
    filters: scopeFilters(scopeLine, industry, companySize, dateFrom, dateTo),
    insights: {
      totalCompanies: analysisReportReady.length,
      bands: labels.map((label, i) => ({
        label,
        count: counts[i],
        companies: bucketDetails[i],
      })),
      densestBand: { label: labels[densestIdx], count: counts[densestIdx] },
    },
  };
}

function buildPillarProfileContext({
  focusOrg,
  compareOrgs,
  compareIds,
  cohortMedians,
  industry,
  companySize,
  dateFrom,
  dateTo,
  scopeLine,
  activePillarKeys,
}) {
  const keys = resolvePillarKeys(activePillarKeys);
  const orgs = collectChartOrgs(
    focusOrg,
    compareOrgs,
    compareIds,
    (o) => o.reportReady
  );

  const orgInsights = orgs.map((o) => {
    const { strongest, weakest } = pillarHighLow(o, keys);
    return {
      name: o.name,
      overall: o.overall != null ? Math.round(o.overall) : null,
      strongest,
      weakest,
    };
  });

  const benchmark = keys
    .map(({ key, label }) => ({
      label,
      value: cohortMedians?.[key] != null ? Math.round(cohortMedians[key]) : null,
    }))
    .filter((p) => p.value != null);

  return {
    chartKey: "pillar_profile",
    chartTitle: "Pillar profile",
    whatThisShows:
      "This visual compares each company's section scores against the group benchmark (median of report-ready companies in the current filter).",
    filters: scopeFilters(scopeLine, industry, companySize, dateFrom, dateTo),
    insights: {
      companies: orgInsights,
      groupBenchmark: benchmark,
      companyCount: orgInsights.length,
    },
  };
}

function buildImportanceContext({
  focusOrg,
  compareOrgs,
  compareIds,
  industry,
  companySize,
  dateFrom,
  dateTo,
  scopeLine,
  activePillarKeys,
}) {
  const keys = resolvePillarKeys(activePillarKeys);
  const orgs = collectChartOrgs(
    focusOrg,
    compareOrgs,
    compareIds,
    orgHasImportanceScores
  );

  const orgInsights = orgs.map((o) => {
    const rows = keys
      .map(({ key, label }) => ({
        label,
        value: getPillarImportance(o, key),
      }))
      .filter((p) => p.value != null && !Number.isNaN(Number(p.value)))
      .map((p) => ({ ...p, value: Math.round(Number(p.value) * 10) / 10 }));
    const sorted = [...rows].sort((a, b) => b.value - a.value);
    return {
      name: o.name,
      highest: sorted[0] || null,
      lowest: sorted.length ? sorted[sorted.length - 1] : null,
    };
  });

  return {
    chartKey: "importance_scores",
    chartTitle: "Importance Scores",
    whatThisShows:
      "This visual shows average importance ratings by section for selected companies compared with the group benchmark line.",
    filters: scopeFilters(scopeLine, industry, companySize, dateFrom, dateTo),
    insights: { companies: orgInsights, scale: "0-9" },
  };
}

function buildAgreementContext({
  focusOrg,
  compareOrgs,
  compareIds,
  industry,
  companySize,
  dateFrom,
  dateTo,
  scopeLine,
  activePillarKeys,
}) {
  const keys = resolvePillarKeys(activePillarKeys);
  const orgs = collectChartOrgs(
    focusOrg,
    compareOrgs,
    compareIds,
    orgHasAgreementScores
  );

  const orgInsights = orgs.map((o) => {
    const rows = keys
      .map(({ key, label }) => ({
        label,
        value: getPillarAgreement(o, key),
      }))
      .filter((p) => p.value != null && !Number.isNaN(Number(p.value)))
      .map((p) => ({ ...p, value: Math.round(Number(p.value) * 10) / 10 }));
    const sorted = [...rows].sort((a, b) => a.value - b.value);
    return {
      name: o.name,
      lowestAgreement: sorted[0] || null,
      highestAgreement: sorted.length ? sorted[sorted.length - 1] : null,
    };
  });

  return {
    chartKey: "agreement_scores",
    chartTitle: "Agreement Scores",
    whatThisShows:
      "This visual shows average team agreement by section for selected companies compared with the group benchmark line.",
    filters: scopeFilters(scopeLine, industry, companySize, dateFrom, dateTo),
    insights: { companies: orgInsights, scale: "1-5" },
  };
}

function buildParticipationContext({
  participationOrgsSlice,
  participationThreeBarActive,
  industry,
  companySize,
  dateFrom,
  dateTo,
  scopeLine,
}) {
  if (participationThreeBarActive && participationOrgsSlice[0]) {
    const o = participationOrgsSlice[0];
    const pct = o.invited ? Math.round((o.completed / o.invited) * 100) : 0;
    return {
      chartKey: "participation",
      chartTitle: "Assessment participation",
      whatThisShows: `This visual shows assessment participation for ${o.name}: full completed, lite completed, pending, and not started participants.`,
      filters: scopeFilters(scopeLine, industry, companySize, dateFrom, dateTo),
      insights: {
        mode: "single_company",
        company: o.name,
        invited: o.invited,
        completed: o.completed,
        liteCompleted: o.liteCompleted || 0,
        pending: o.pending,
        notStarted: o.notStarted,
        completionPct: pct,
      },
    };
  }

  const rows = participationOrgsSlice.map((o) => ({
    name: o.name,
    invited: o.invited,
    completed: o.completed,
    liteCompleted: o.liteCompleted || 0,
    completionPct: o.invited ? Math.round((o.completed / o.invited) * 100) : 0,
  }));
  const byCompletion = [...rows].sort((a, b) => b.completionPct - a.completionPct);
  const best = byCompletion[0] || null;
  const worst = byCompletion.length ? byCompletion[byCompletion.length - 1] : null;

  return {
    chartKey: "participation",
    chartTitle: "Assessment participation",
    whatThisShows:
      "This visual shows stacked participation by company: full completed, lite completed, pending, and not started participants.",
    filters: scopeFilters(scopeLine, industry, companySize, dateFrom, dateTo),
    insights: {
      mode: "multi_company",
      companies: rows,
      highestCompletion: best,
      lowestCompletion: worst,
    },
  };
}

function buildWeeklyCompletionsContext({
  analysisOrgs,
  focusOrgId,
  compareIds,
  compareOrgs,
  focusOrg,
  weekCategories,
  weeklyTrend,
  industry,
  companySize,
  dateFrom,
  dateTo,
  scopeLine,
}) {
  const weeks = weekCategories || [];
  let seriesLabel = "portfolio aggregate";
  let values = [];

  if (focusOrgId && focusOrg) {
    seriesLabel = focusOrg.name;
    const row = weeklyTrend?.completionsByOrgId?.[focusOrg.id];
    values = weeks.map((_, i) => (row ? Math.max(0, Math.round(row[i] ?? 0)) : 0));
  } else if (compareIds.length >= 2 && compareOrgs.length >= 2) {
    seriesLabel = "selected companies";
    values = weeks.map((_, wi) => {
      let sum = 0;
      compareOrgs.forEach((org) => {
        const row = weeklyTrend?.completionsByOrgId?.[org.id];
        if (row) sum += row[wi] ?? 0;
      });
      return Math.max(0, Math.round(sum));
    });
  } else {
    values = weeks.map((_, wi) => {
      let sum = 0;
      analysisOrgs.forEach((org) => {
        const row = weeklyTrend?.completionsByOrgId?.[org.id];
        if (row) sum += row[wi] ?? 0;
      });
      return Math.max(0, Math.round(sum));
    });
  }

  let peakIdx = 0;
  values.forEach((v, i) => {
    if (v > values[peakIdx]) peakIdx = i;
  });
  const total = values.reduce((a, b) => a + b, 0);

  return {
    chartKey: "weekly_completions",
    chartTitle: "Weekly completions",
    whatThisShows:
      "This visual tracks how many full and lite assessments were completed each week for the companies in the current scope.",
    filters: scopeFilters(scopeLine, industry, companySize, dateFrom, dateTo),
    insights: {
      seriesLabel,
      totalCompletions: total,
      peakWeek: weeks[peakIdx] || null,
      peakCount: values[peakIdx] ?? 0,
      weekCount: weeks.length,
    },
  };
}

export function buildInsightsChartExplainContexts(params) {
  return {
    performance_ranking: buildPerformanceRankingContext(params),
    score_band: buildScoreBandContext(params),
    pillar_profile: buildPillarProfileContext(params),
    importance_scores: buildImportanceContext(params),
    agreement_scores: buildAgreementContext(params),
    participation: buildParticipationContext(params),
    weekly_completions: buildWeeklyCompletionsContext(params),
  };
}

/** Format explanation text from structured chart context (matches backend templates). */
export function formatChartExplanation(ctx) {
  if (!ctx?.chartKey || !ctx?.whatThisShows) {
    return ctx?.whatThisShows || "This chart summarizes the current filtered view.";
  }

  const { chartKey, whatThisShows, insights = {} } = ctx;
  const parts = [whatThisShows];

  if (chartKey === "performance_ranking") {
    const n = insights.companyCount || 0;
    if (!n) {
      parts.push("No report-ready companies match the current filters.");
      return parts.join(" ");
    }
    const { best, worst, spread } = insights;
    if (best?.name) {
      let line = `The best performer is ${best.name} with an overall score of ${best.overall} (${best.percentile}th percentile).`;
      if (worst?.name && worst.name !== best.name) {
        line += ` The lowest in this view is ${worst.name} at ${worst.overall} (${worst.percentile}th percentile).`;
        if (spread != null) line += ` That is a ${spread}-point spread across the ${n} companies shown.`;
      }
      parts.push(line);
    }
    return parts.join(" ");
  }

  if (chartKey === "score_band") {
    const total = insights.totalCompanies || 0;
    if (!total) {
      parts.push("No report-ready companies are in scope for the current filters.");
      return parts.join(" ");
    }
    const d = insights.densestBand || {};
    parts.push(
      `There are ${total} report-ready companies in this view. The largest concentration is in the ${d.label || "—"} band with ${d.count || 0} companies.`
    );
    return parts.join(" ");
  }

  if (chartKey === "pillar_profile") {
    const companies = insights.companies || [];
    if (!companies.length) {
      parts.push("Select a focused or compared report-ready company to see pillar-level strengths and gaps.");
      return parts.join(" ");
    }
    if (companies.length === 1) {
      const line = pillarProfileCompanyLine(companies[0]);
      if (line) parts.push(`${line}, versus the group benchmark.`);
      return parts.join(" ");
    }
    const lines = companies.map(pillarProfileCompanyLine).filter(Boolean);
    if (lines.length) {
      parts.push(
        `Comparing ${companies.length} companies (pillar scores vs group benchmark): ${lines.join("; ")}.`
      );
    }
    return parts.join(" ");
  }

  if (chartKey === "importance_scores") {
    const companies = insights.companies || [];
    if (!companies.length) {
      parts.push("Importance ratings appear when a report-ready company with survey data is in focus or compare.");
      return parts.join(" ");
    }
    if (companies.length === 1) {
      const line = importanceCompanyLine(companies[0]);
      if (line) parts.push(`${line} (0–9 scale).`);
      return parts.join(" ");
    }
    const lines = companies.map(importanceCompanyLine).filter(Boolean);
    if (lines.length) {
      parts.push(
        `Comparing ${companies.length} companies on importance (0–9 scale): ${lines.join("; ")}.`
      );
    }
    return parts.join(" ");
  }

  if (chartKey === "agreement_scores") {
    const companies = insights.companies || [];
    if (!companies.length) {
      parts.push("Agreement ratings appear when a report-ready company with survey data is in focus or compare.");
      return parts.join(" ");
    }
    if (companies.length === 1) {
      const line = agreementCompanyLine(companies[0]);
      if (line) parts.push(`${line} (1–5 scale).`);
      return parts.join(" ");
    }
    const lines = companies.map(agreementCompanyLine).filter(Boolean);
    if (lines.length) {
      parts.push(
        `Comparing ${companies.length} companies on team agreement (1–5 scale): ${lines.join("; ")}.`
      );
    }
    return parts.join(" ");
  }

  if (chartKey === "participation") {
    if (insights.mode === "single_company") {
      parts.push(
        `${insights.company} has ${insights.completed} of ${insights.invited} participants completed (${insights.completionPct}%), with ${insights.pending} pending and ${insights.notStarted} not started.`
      );
      return parts.join(" ");
    }
    const hi = insights.highestCompletion;
    const lo = insights.lowestCompletion;
    if (hi && lo) {
      parts.push(
        `${hi.name} has the highest completion rate (${hi.completionPct}% — ${hi.completed}/${hi.invited}), while ${lo.name} is lowest at ${lo.completionPct}% (${lo.completed}/${lo.invited}).`
      );
    }
    return parts.join(" ");
  }

  if (chartKey === "weekly_completions") {
    const total = insights.totalCompletions || 0;
    if (!total) {
      parts.push(`No completions were recorded in the selected period for ${insights.seriesLabel || "this scope"}.`);
    } else {
      parts.push(
        `For ${insights.seriesLabel}, ${total} completions were recorded across ${insights.weekCount || 0} weeks. The busiest week was ${insights.peakWeek} with ${insights.peakCount} completions.`
      );
    }
    return parts.join(" ");
  }

  if (chartKey === "invited_participants") {
    parts.push(
      `Across ${insights.periodCount || 0} periods, ${insights.totalInvitesSent || 0} invites were sent and ${insights.totalCompleted || 0} assessments were completed. The peak completion period was ${insights.peakCompletedPeriod || "—"} with ${insights.peakCompletedCount || 0} completions.`
    );
    return parts.join(" ");
  }

  if (chartKey === "current_statistics") {
    const total = insights.total || 0;
    const largest = insights.largestStatus;
    if (!total) parts.push("No assessment status data is available yet.");
    else if (largest?.name) {
      parts.push(
        `Out of ${total} assessments, the largest group is ${largest.name} (${largest.count} — ${largest.pct}% of the total).`
      );
    }
    return parts.join(" ");
  }

  if (chartKey === "lite_to_full_conversion") {
    const { liteFinished = 0, fullStarted = 0, fullCompleted = 0, conversionPct = 0, awaitingUpgrade = 0, upgradeDeclined = 0 } = insights;
    if (!liteFinished) {
      parts.push("No lite assessments have been completed yet.");
    } else {
      parts.push(
        `Of the ${liteFinished} participants who finished the lite assessment, ${fullStarted} (${conversionPct}%) chose to start the full assessment and ${fullCompleted} have completed it.`
      );
      if (awaitingUpgrade > 0) parts.push(`${awaitingUpgrade} are still deciding whether to upgrade.`);
      if (upgradeDeclined > 0) parts.push(`${upgradeDeclined} opted to upgrade 'later'.`);
    }
    return parts.join(" ");
  }

  if (chartKey === "full_pillar_funnel" || chartKey === "lite_pillar_funnel") {
    const label = chartKey === "full_pillar_funnel" ? "full" : "lite";
    const { started = 0, completed = 0, completionPct = 0, topDropOff } = insights;
    if (!started) {
      parts.push(`No ${label} assessments have been started yet.`);
    } else {
      parts.push(
        `${started} participants started the ${label} assessment, of whom ${completed} (${completionPct}%) completed it.`
      );
      if (topDropOff?.title && topDropOff.drop_off_pct > 0) {
        parts.push(
          `The highest drop-off step is ${topDropOff.title} (${topDropOff.drop_off_pct}% of starters stopped there).`
        );
      }
    }
    return parts.join(" ");
  }

  return whatThisShows;
}

export function buildAnalyticsDropOffExplainContexts({ dropOffData }) {
  const liteToFull = dropOffData?.lite_to_full || {};
  const fullFunnel = dropOffData?.full_pillar_funnel || {};
  const liteFunnel = dropOffData?.lite_pillar_funnel || {};

  return {
    lite_to_full_conversion: {
      chartKey: "lite_to_full_conversion",
      chartTitle: "Lite → Full upgrade",
      whatThisShows:
        "This panel shows how many participants who completed the lite assessment chose to upgrade to the full assessment, and how many finished it.",
      insights: {
        liteFinished: liteToFull.lite_finished || 0,
        fullStarted: liteToFull.full_started || 0,
        fullCompleted: liteToFull.full_completed || 0,
        conversionPct: liteToFull.conversion_pct || 0,
        awaitingUpgrade: liteToFull.awaiting_upgrade || 0,
        upgradeDeclined: liteToFull.upgrade_declined || 0,
      },
    },
    full_pillar_funnel: {
      chartKey: "full_pillar_funnel",
      chartTitle: "Full assessment drop-off",
      whatThisShows:
        "This panel shows step-by-step where participants drop off in the full assessment funnel — how many reached each pillar and how many stopped there.",
      insights: {
        started: fullFunnel.started_count || 0,
        completed: fullFunnel.completed_count || 0,
        completionPct: fullFunnel.completion_pct || 0,
        topDropOff: fullFunnel.top_drop_off
          ? {
              title: fullFunnel.top_drop_off.title,
              drop_off_pct: fullFunnel.top_drop_off.drop_off_pct,
              drop_off_count: fullFunnel.top_drop_off.drop_off_count,
            }
          : null,
        steps: (fullFunnel.steps || []).map((s) => ({
          step: s.step,
          title: s.title,
          reached_count: s.reached_count,
          drop_off_count: s.drop_off_count,
          drop_off_pct: s.drop_off_pct,
        })),
      },
    },
    lite_pillar_funnel: {
      chartKey: "lite_pillar_funnel",
      chartTitle: "Lite assessment drop-off",
      whatThisShows:
        "This panel shows step-by-step where participants drop off in the lite assessment funnel — how many reached each pillar step and how many stopped there.",
      insights: {
        started: liteFunnel.started_count || 0,
        completed: liteFunnel.completed_count || 0,
        completionPct: liteFunnel.completion_pct || 0,
        topDropOff: liteFunnel.top_drop_off
          ? {
              title: liteFunnel.top_drop_off.title,
              drop_off_pct: liteFunnel.top_drop_off.drop_off_pct,
              drop_off_count: liteFunnel.top_drop_off.drop_off_count,
            }
          : null,
        steps: (liteFunnel.steps || []).map((s) => ({
          step: s.step,
          title: s.title,
          reached_count: s.reached_count,
          drop_off_count: s.drop_off_count,
          drop_off_pct: s.drop_off_pct,
        })),
      },
    },
  };
}

export function buildAnalyticsChartExplainContexts({ barData, filterType, pieData }) {
  const categories = (barData || []).map((item) =>
    filterType === "months" ? item.month : filterType === "weeks" ? item.day : item.hour
  );
  const pending = (barData || []).map((item) => item.pending || 0);
  const sent = (barData || []).map((item) => item.invite_sent || 0);
  const liteCompleted = (barData || []).map((item) => item.lite_completed || 0);
  const completed = (barData || []).map((item) => item.completed || 0);
  const totalCompleted = completed.reduce((a, b) => a + b, 0);
  const totalLiteCompleted = liteCompleted.reduce((a, b) => a + b, 0);
  const totalSent = sent.reduce((a, b) => a + b, 0);

  let peakCompletedIdx = 0;
  completed.forEach((v, i) => {
    if (v > completed[peakCompletedIdx]) peakCompletedIdx = i;
  });

  const series = pieData?.seriesData || [];
  const total = pieData?.total || 0;
  const sortedSlices = [...series].sort((a, b) => (b.y || 0) - (a.y || 0));
  const topSlice = sortedSlices[0] || null;
  const topPct = total && topSlice ? Math.round((topSlice.y / total) * 100) : null;

  const granularity =
    filterType === "days" ? "daily" : filterType === "weeks" ? "weekly" : "monthly";

  return {
    invited_participants: {
      chartKey: "invited_participants",
      chartTitle: "Invited Participants",
      whatThisShows: `This visual shows ${granularity} invite activity: pending, invites sent, lite completed, and full completed assessments over time.`,
      filters: { trendGranularity: filterType },
      insights: {
        periodCount: categories.length,
        totalInvitesSent: totalSent,
        totalCompleted,
        totalLiteCompleted,
        peakCompletedPeriod: categories[peakCompletedIdx] || null,
        peakCompletedCount: completed[peakCompletedIdx] ?? 0,
      },
    },
    current_statistics: {
      chartKey: "current_statistics",
      chartTitle: "Current Statistics",
      whatThisShows:
        "This visual shows the share of all assessments in each status (full completed, lite completed, pending, and not started).",
      filters: { trendGranularity: filterType },
      insights: {
        total,
        slices: series.map((s) => ({
          name: s.name,
          count: s.y,
          pct: total ? Math.round((s.y / total) * 100) : 0,
        })),
        largestStatus: topSlice
          ? { name: topSlice.name, count: topSlice.y, pct: topPct }
          : null,
      },
    },
  };
}
